import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Typography
} from '@mui/material';
import { getApplication } from '../services/applicationService';
import { API_URL } from '../config';
import ApplicationHeader from '../components/application/ApplicationHeader';
import JobInfoSection from '../components/application/JobInfoSection';

import ResumeBuilderSection from '../components/application/ResumeBuilderSection';
import { integrateGraphData } from '../utils/graphDataAdapter';
import { mockAuthHeaders } from '../utils/mockAuth';

/**
 * Application Detail Page - Centralized hub for managing a single application
 * 
 * This replaces the old multi-stage workflow with a single unified view.
 * All information and actions for one application are accessible from this page.
 */
const ApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [jobDescriptionData, setJobDescriptionData] = useState(null);
  
  // Graph data state (integrated view)
  const [graphData, setGraphData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [matchingResults, setMatchingResults] = useState(null);
  const [resumeConfig, setResumeConfig] = useState(null);

  // Load application data
  useEffect(() => {
    loadApplicationData();
  }, [id]);
  
  // Integrate data into graph model whenever sources change
  useEffect(() => {
    if (profile && jobDescriptionData) {
      const integrated = integrateGraphData(
        profile,
        jobDescriptionData,
        matchingResults,
        resumeConfig
      );

      setGraphData(integrated);
    }
  }, [profile, jobDescriptionData, matchingResults, resumeConfig]);
  
  // Handle selection toggle
  const handleToggleSelection = async (itemId) => {
    if (!graphData) return;
    
    // Toggle in graphData (mutates the object)
    graphData.toggleSelection(itemId);

    // Get updated selected IDs - use item_id!
    const selectedIds = graphData.getSelectedNodes().map(n => n.item_id);
    
    // Save to backend
    try {
      const response = await fetch(`${API_URL}/api/applications/${id}/resume-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ selected_items: selectedIds })
      });
      
      if (!response.ok) {
        console.error('[ApplicationDetailPage] Save failed:', response.status);
      }
    } catch (err) {
      console.error('[ApplicationDetailPage] Error saving selection:', err);
    }
    
    // Force re-render by creating new object reference
    // This ensures React detects the change
    setGraphData({ ...graphData });
  };

  const loadApplicationData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load application metadata
      const appData = await getApplication(id);
      setApplicationData(appData);

      // Load full job description with criteria (if available)
      const jdResponse = await fetch(`${API_URL}/api/applications/${id}/job-description`, {
        headers: {
          ...mockAuthHeaders()
        },
        credentials: 'include',
      });

      if (jdResponse.ok) {
        const jdData = await jdResponse.json();
        // Add application ID for match analysis loading
        jdData.application_id = id;
        setJobDescriptionData(jdData);
      } else {
        console.error('[ApplicationDetailPage] Failed to load JD:', jdResponse.status, jdResponse.statusText);
        const errorText = await jdResponse.text();
        console.error('[ApplicationDetailPage] Error response:', errorText);
      }

      // Load profile
      const profileResponse = await fetch(`${API_URL}/api/profile-manager`, {
        headers: { ...mockAuthHeaders() },
        credentials: 'include',
      });
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setProfile(profileData);
      } else {
        console.error('[ApplicationDetailPage] Failed to load profile:', profileResponse.status, profileResponse.statusText);
        const errorText = await profileResponse.text();
        console.error('[ApplicationDetailPage] Profile error:', errorText);
        setProfile(null);
      }

      // Load matching results (optional)
      try {
        const matchingResponse = await fetch(`${API_URL}/api/applications/${id}/matching-results`, {
          headers: { ...mockAuthHeaders() },
          credentials: 'include',
        });
        if (matchingResponse.ok) {
          const matchingData = await matchingResponse.json();
          setMatchingResults(matchingData);
        }
      } catch (err) {
        // No matching results yet
      }

      // Load resume config (optional)
      try {
        const configResponse = await fetch(`${API_URL}/api/applications/${id}/resume-config`, {
          headers: { ...mockAuthHeaders() },
          credentials: 'include',
        });
        if (configResponse.ok) {
          const configData = await configResponse.json();
          setResumeConfig(configData);
        }
      } catch (err) {
        // No resume config yet
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading application:', err);
      setError(err.message || 'Failed to load application');
      setLoading(false);
    }
  };

  // Handle application updates
  const handleApplicationUpdate = async (updates) => {
    try {
      // Update local state immediately for responsiveness
      setApplicationData(prev => ({ ...prev, ...updates }));
      
      // TODO: Auto-save to backend (will implement with resume builder)
    } catch (err) {
      console.error('Error updating application:', err);
      setError(err.message || 'Failed to update application');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 }, py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 }, py: 4 }}>
        <Alert severity="error" onClose={() => navigate('/applications')}>
          {error}
        </Alert>
      </Container>
    );
  }

  // No data state
  if (!applicationData) {
    return (
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 }, py: 4 }}>
        <Alert severity="warning" onClose={() => navigate('/applications')}>
          Application not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        py: 3
      }}
    >
      {/* Application Header - Always visible */}
      <ApplicationHeader
        application={applicationData}
        jobDescription={jobDescriptionData}
        onUpdate={handleApplicationUpdate}
        onRefresh={loadApplicationData}
      />

      {/* Job Info Section - Tabs for Job Details, Company Insights, and Original Job Description */}
      <Box mt={3}>
        <JobInfoSection
          application={applicationData}
          jobDescription={jobDescriptionData}
        />
      </Box>

      {/* Resume Builder Section - Phase 1B */}
      <Box mt={2} mb={4}>
        <ResumeBuilderSection
          application={applicationData}
          profile={profile}
          jobDescription={jobDescriptionData}
          graphData={graphData}
          onToggleSelection={handleToggleSelection}
          onUpdate={handleApplicationUpdate}
        />
      </Box>
    </Container>
  );
};

export default ApplicationDetailPage;
